import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import APIService from '../../../Services/APIService';
import { ConstantActions, LeadFormActions } from '../../../Services/_actions';
import { Apis } from '../../../Services/app.constants';
import {
  formEnableDisableAtom,
  leadDetailObjAtom,
  leadFormQuickTemplateAtom,
  referenceFieldAtom,
  userInfoByNumberAtom,
  userRawLeadFormDataAtom,
} from '../../../Services/_states/LeadForm';
import { validateFormFieldValue } from '../../../components/Leads/DynamicFormFields/helper.ts';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { interveneLeadCampaignAtom } from '../state/interveneAtoms';
import { campaignLeadManagementAtom } from '../../../Services/_states/index.js';
import useRecordPermissions from '../../business-functions/records/hooks/useRecordPermissions';
import { recordService } from '../../business-functions/records/services/recordService';
import {
  buildAgentLeadFormData,
  isLeadIdField,
  isRequiredLeadFormField,
  isSystemManagedLeadField,
  isVisibleLeadFormField,
} from '../utils/leadFormFields';

export default function useLeadFormContext({
  phone,
  isDrawerOpen,
  organisationId,
}) {
  const leadFormActions = LeadFormActions();
  const constantActions = ConstantActions();
  const { showSnackBar } = useSnackBar();

  const [leadCampaign, setLeadCampaign] = useRecoilState(interveneLeadCampaignAtom);
  const [campaignLeadManagement, setCampaignLeadManagement] = useRecoilState(campaignLeadManagementAtom);
  const [leadDetails, setLeadDetail] = useRecoilState(leadDetailObjAtom);
  const [userDetailByNumber, setUserDetailByNumber] = useRecoilState(userInfoByNumberAtom);
  const [userRawLeadData, setUserRawLeadData] = useRecoilState(userRawLeadFormDataAtom);
  const [formEnable, setFormEnable] = useRecoilState(formEnableDisableAtom);
  const setReferences = useSetRecoilState(referenceFieldAtom);
  const setLeadFormTemplate = useSetRecoilState(leadFormQuickTemplateAtom);

  const [dynamicFormData, setDynamicFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [phoneError, setPhoneError] = useState('');
  const [statusUpdate, setStatusUpdate] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formDataLoading, setFormDataLoading] = useState(false);
  const [listRefreshToken, setListRefreshToken] = useState(0);
  const [isBlankCreateMode, setIsBlankCreateMode] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [formPhone, setFormPhone] = useState(phone || '');

  const lastLoadedContextRef = useRef('');

  useEffect(() => {
    setFormPhone(phone || '');
  }, [phone]);

  const hasCampaignContext = Boolean(
    leadCampaign?.campaign?.proposal_id && leadCampaign?.webForm?.leads_form_id
  );

  const campaignId = leadCampaign?.campaign?.proposal_id || userDetailByNumber?.campaign_id || '';
  const leadFormId = leadCampaign?.webForm?.leads_form_id || userDetailByNumber?.leadform_Id || '';
  const recordPermissions = useRecordPermissions(campaignId);

  useEffect(() => {
    if (!isDrawerOpen || !hasCampaignContext || !leadFormId) return undefined;

    let cancelled = false;
    const loadStatusOptions = async () => {
      try {
        const res = await APIService.get(
          `${Apis.statusSubStatus}?leads_form_id=${encodeURIComponent(leadFormId)}&active=true&type_of_status=`
        );
        if (cancelled) return;

        const payload = res?.data;
        const statusList = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.list)
            ? payload.list
            : [];

        setCampaignLeadManagement((prev) => ({
          ...prev,
          statusData: {
            ...(prev?.statusData || {}),
            [leadFormId]: statusList,
          },
        }));
      } catch {
        // Keep the selected form usable if status options cannot be loaded.
      }
    };

    loadStatusOptions();
    return () => {
      cancelled = true;
    };
  }, [isDrawerOpen, hasCampaignContext, leadFormId, setCampaignLeadManagement]);

  const processLeadData = useCallback((data, webForm) => {
    if (!webForm) return;

    const normalizedLead = {
      ...(data || {}),
      _mongoId: data?._mongoId || data?.id,
      id: data?._mongoId || data?.id || data?.lead_id,
      lead_id: data?.lead_id || data?.id,
    };
    setLeadDetail(normalizedLead);

    if (normalizedLead?.id || normalizedLead?.lead_id) setFormEnable(true);

    setLeadFormTemplate(webForm?.templates ?? []);
    if (webForm?.reference) setReferences(webForm.reference);

    setDynamicFormData(buildAgentLeadFormData(webForm, normalizedLead, { includeLeadId: true }));
    setIsFormDirty(false);
  }, [setFormEnable, setLeadDetail, setLeadFormTemplate, setReferences]);

  const initBlankForm = useCallback((webForm, campaign) => {
    if (!webForm?.leads_form_items && !webForm?.data) return;

    setDynamicFormData(buildAgentLeadFormData(webForm, null, { includeLeadId: true }));
    setIsFormDirty(false);
    setLeadFormTemplate(webForm?.templates ?? []);
    if (webForm?.reference) setReferences(webForm.reference);
    setFormEnable(false);
    setUserDetailByNumber((prev) => ({
      ...prev,
      leadform_Id: webForm.leads_form_id,
      campaign_id: campaign?.proposal_id,
      phone_number: phone || prev?.phone_number || '',
    }));
  }, [phone, setFormEnable, setLeadFormTemplate, setReferences, setUserDetailByNumber]);

  const loadLeadByPhone = useCallback(async () => {
    if (!phone || !isDrawerOpen) return;
    if (!leadCampaign?.campaign?.proposal_id || !leadCampaign?.webForm?.leads_form_id) return;

    const contextKey = `${phone}:${campaignId}:${leadFormId}`;
    setFormDataLoading(true);
    try {
      const res = await leadFormActions.getDetailsByNumber(phone);
      if (res?.status === 200 && res?.data && res.data !== 'Not found') {
        const data = { ...res.data };
        let updateData = [...(data?.data || [])];
        updateData.forEach((element) => {
          if (element?.column_name === 'supplier_id') data.supplier_id = element.value || '';
          if (element?.column_name === 'city') data.city = element.value || '';
          if (element?.column_name === 'area') data.area = element.value || '';
        });
        setUserRawLeadData({ ...data, data: updateData });
        setUserDetailByNumber({
          ...data,
          leadform_Id: leadFormId,
          campaign_id: campaignId,
          phone_number: phone,
        });
        setIsBlankCreateMode(false);
        initBlankForm(leadCampaign.webForm, leadCampaign.campaign);
      } else {
        const rawRes = await leadFormActions.getRawLeadForm();
        if (rawRes?.status === 200 && rawRes?.data?.data) {
          setUserRawLeadData({ ...rawRes.data });
        }
        setIsBlankCreateMode(false);
        initBlankForm(leadCampaign.webForm, leadCampaign.campaign);
        if (res?.data === 'Not found') {
          showSnackBar('User not found', 'warning');
        }
      }
    } catch (err) {
      console.error('Failed to load lead context', err);
      showSnackBar(err?.toString() || 'Failed to load lead', 'error');
    } finally {
      setFormDataLoading(false);
      lastLoadedContextRef.current = contextKey;
    }
  }, [
    phone,
    isDrawerOpen,
    leadCampaign,
    leadFormId,
    campaignId,
    leadFormActions,
    initBlankForm,
    setUserDetailByNumber,
    setUserRawLeadData,
    showSnackBar,
  ]);

  useEffect(() => {
    if (!isDrawerOpen || !phone || !hasCampaignContext) return;

    const contextKey = `${phone}:${campaignId}:${leadFormId}`;
    if (lastLoadedContextRef.current === contextKey) return;

    loadLeadByPhone();
  }, [isDrawerOpen, phone, hasCampaignContext, campaignId, leadFormId, loadLeadByPhone]);

  const applyCampaignFormSelection = useCallback((data) => {
    if (data?.campaign && data?.webForm) {
      setLeadCampaign(data);
      setCampaignLeadManagement((prev) => ({
        ...prev,
        selectedForm: data.webForm,
        selectedCampaign: data.campaign,
      }));
      setUserDetailByNumber((prev) => ({
        ...prev,
        leadform_Id: data.webForm.leads_form_id,
        campaign_id: data.campaign.proposal_id,
        phone_number: phone || prev.phone_number,
      }));
      setLeadDetail({});
      setDynamicFormData({});
      setUserRawLeadData({});
      setFieldErrors({});
      setStatusUpdate({});
      setIsFormDirty(false);
      setIsBlankCreateMode(false);
      lastLoadedContextRef.current = '';
      initBlankForm(data.webForm, data.campaign);
    }
  }, [
    phone,
    initBlankForm,
    setCampaignLeadManagement,
    setLeadCampaign,
    setLeadDetail,
    setUserDetailByNumber,
    setUserRawLeadData,
  ]);

  const selectLeadFromList = useCallback((lead) => {
    const leadId = lead?.id;
    if (!leadId || !leadCampaign?.webForm) return;

    setIsBlankCreateMode(false);
    setFieldErrors({});
    setStatusUpdate({});
    processLeadData(lead, leadCampaign.webForm);
  }, [leadCampaign?.webForm, processLeadData]);

  const handleFieldChange = useCallback((index, value, groupTitle) => {
    const updatedData = { ...dynamicFormData };
    const groupForm = [...(updatedData[groupTitle] || [])];
    let updatedField = { ...groupForm[index], value };

    if (value && typeof value === 'object' && value?.collection === 'entity') {
      updatedField = { ...updatedField, entity_data: value ?? null, value: value?.value ?? '' };
    } else if (
      updatedField?.statusOption === 'is_status' ||
      updatedField?.statusOption === 'is_sub_status'
    ) {
      if (updatedField?.statusOption === 'is_status') {
        updatedField = {
          ...updatedField,
          status_config: value ?? null,
          value: value?.status_name ?? '',
        };
      } else if (updatedField?.statusOption === 'is_sub_status') {
        updatedField = { ...updatedField, status_config: value ?? null, value: value?.name ?? '' };
      }
      setStatusUpdate((prev) => {
        if (updatedField?.statusOption === 'is_status') {
          return {
            ...prev,
            status: {
              ...prev.status,
              [updatedField.key_name]: {
                status_id: value?.status_id ?? '',
                status_name: value?.status_name ?? '',
                type_of_status: value?.type_of_status || updatedField?.key_name || '',
                status: value?.status_name ?? '',
              },
            },
          };
        }
        if (updatedField?.statusOption === 'is_sub_status') {
          return {
            ...prev,
            sub_status: {
              ...(prev?.sub_status ?? {}),
              [updatedField.key_name]: {
                sub_status_id: value?.sub_status_id ?? '',
                sub_status_name: value?.name ?? '',
                type_of_status: value?.type_of_status ?? updatedField?.key_name ?? '',
                status_id: value?.status_id ?? '',
                status_name: value?.status_name ?? '',
              },
            },
          };
        }
        return prev;
      });
    }

    groupForm[index] = updatedField;
    updatedData[groupTitle] = groupForm;
    setDynamicFormData(updatedData);
    setIsFormDirty(true);

    const errorKey = `${groupTitle}_${index}`;
    const isEmpty = value === '' || value === null || value === undefined
      || (typeof value === 'string' && value.trim() === '');
    const errorMsg = validateFormFieldValue(updatedField, value);
    if (isRequiredLeadFormField(updatedField) && isEmpty) {
      setFieldErrors((prev) => ({ ...prev, [errorKey]: 'Field is required' }));
    } else if (errorMsg) {
      setFieldErrors((prev) => ({ ...prev, [errorKey]: errorMsg }));
    } else {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  }, [dynamicFormData]);

  const handlePhoneChange = useCallback((value) => {
    setFormPhone(value);
    if (String(value || '').trim()) setPhoneError('');
  }, []);

  const uploadFileInS3 = useCallback(async (files) => {
    try {
      const file = files?.[0];
      if (!file) return '';
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);
      const resp = await constantActions.uploadFileS3Bucket(formDataToUpload);
      if (resp?.status === 200 && resp?.data?.data) {
        return resp.data.data.url ?? '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
    return '';
  }, [constantActions]);

  const updateStatusInLoop = useCallback(async (leadId) => {
    if (!leadId) return;
    const statusPayloads = [
      ...Object.values(statusUpdate?.status ?? {}),
      ...Object.values(statusUpdate?.sub_status ?? {}),
    ]
      .filter(
        (item) => Boolean(item) && item?.status_id && (item?.status_name || item?.sub_status_name)
      )
      .map((item) => ({ ...item, id: leadId }));

    if (statusPayloads.length === 0) return;

    await Promise.allSettled(
      statusPayloads.map((payload) => leadFormActions.updateLeadStatusDate(payload))
    );
  }, [leadFormActions, statusUpdate]);

  const saveForms = useCallback(async () => {
    const currentLeadId = isBlankCreateMode
      ? ''
      : leadDetails?._mongoId
        || leadDetails?.id
        || userDetailByNumber?.id
        || userDetailByNumber?.leadId
        || '';
    const isCreatingLead = !currentLeadId;
    const hasSavePermission = isCreatingLead
      ? recordPermissions.canCreate
      : recordPermissions.canEdit;
    if (!hasSavePermission) {
      showSnackBar(
        isCreatingLead
          ? 'You do not have permission to add records'
          : 'You do not have permission to edit records',
        'error'
      );
      return;
    }

    const normalizedPhone = `${formPhone || ''}`.trim();
    if (!leadFormId) {
      showSnackBar('Form ID is missing', 'error');
      return;
    }

    let allFields = [];
    Object.values(dynamicFormData).forEach((group) => {
      allFields = allFields.concat(group);
    });

    const errors = {};
    Object.entries(dynamicFormData).forEach(([groupTitle, fields]) => {
      fields.forEach((item, index) => {
        if (!isVisibleLeadFormField(item)) return;
        if (item?.key_name === 'Q2. Designation') return;

        const value = item?.value;
        const isEmpty = value === '' || value === null || value === undefined
          || (typeof value === 'string' && value.trim() === '')
          || (Array.isArray(value) && value.length === 0);
        const errorKey = `${groupTitle}_${index}`;
        if (isRequiredLeadFormField(item) && isEmpty) {
          errors[errorKey] = 'Field is required';
        } else if (!isEmpty) {
          const valueError = validateFormFieldValue(item, value);
          if (valueError) errors[errorKey] = valueError;
        }
      });
    });

    setFieldErrors(errors);
    setPhoneError(normalizedPhone ? '' : 'Phone number is required');
    if (Object.keys(errors).length > 0 || !normalizedPhone) return;

    setIsLoading(true);

    const updatedField = allFields.filter(
      (item) =>
        !item?.is_static_column &&
        (!isSystemManagedLeadField(item) || isLeadIdField(item))
    );
    const entries = await Promise.all(
      updatedField.map(async (item) => {
        if (item.file?.length) {
          const fileUrl = await uploadFileInS3(item.file);
          return { key_name: item.key_name, value: fileUrl };
        }
        return {
          key_name: item.key_name,
          value: item?.entity_data?.value ? item?.entity_data?.value ?? '' : item.value ?? '',
          ...(item?.entity_data && { entity_data: item?.entity_data ?? null }),
          ...(item?.status_config && { status_config: item?.status_config ?? null }),
        };
      })
    );

    const data = {
      supplier_id: userDetailByNumber?.supplier_id?.toString() || '',
      city: userDetailByNumber?.city || '',
      area: userDetailByNumber?.area || '',
      phone_number: normalizedPhone,
      supplier_name: userDetailByNumber?.entity_name || '',
      contact_name: userDetailByNumber?.person_name || '',
      leads_form_entries: entries,
      internal_comment: leadDetails?.internal_comment || '',
    };

    try {
      const recordPayload = {
        area: data.area,
        city: data.city,
        lead_id: leadDetails?.lead_id || userDetailByNumber?.lead_id || currentLeadId,
        leads_form_entries: entries,
        phone_number: normalizedPhone,
        supplier_id: data.supplier_id,
        supplier_name: data.supplier_name,
        leads_form_id: Number.parseInt(String(leadFormId), 10) || leadFormId,
        parent_id: { lead_id: leadDetails?.parent_id?.lead_id || '' },
      };
      const res = currentLeadId
        ? await recordService.updateRecord(recordPayload)
        : await recordService.createRecord(recordPayload);
      if (res?.status === 200 && res?.data?.status !== false) {
        const mongoLeadId =
          currentLeadId ||
          res?.data?.data?._id ||
          res?.data?.data?.id ||
          res?.data?.id ||
          res?.data?.data?.lead_id ||
          '';
        const displayLeadId =
          res?.data?.data?.lead_id ||
          leadDetails?.lead_id ||
          mongoLeadId;

        setLeadDetail((prev) => ({
          ...prev,
          _mongoId: prev?._mongoId || mongoLeadId,
          id: prev?.id || mongoLeadId,
          lead_id: prev?.lead_id || displayLeadId,
          data: entries,
        }));
        if (displayLeadId) {
          setDynamicFormData((prev) =>
            Object.fromEntries(
              Object.entries(prev).map(([groupTitle, fields]) => [
                groupTitle,
                fields.map((field) =>
                  isLeadIdField(field) ? { ...field, value: displayLeadId } : field
                ),
              ])
            )
          );
        }
        await updateStatusInLoop(mongoLeadId);
        setStatusUpdate({});
        showSnackBar('Lead saved successfully', 'success');
        setFormEnable(true);
        setIsFormDirty(false);
        setIsBlankCreateMode(false);
        setListRefreshToken((token) => token + 1);
      } else {
        const errorCode = res?.data?.data?.general_error?.msg
          || res?.data?.general_error?.msg;
        showSnackBar(
          errorCode === 'not_permitted'
            ? 'You do not have permission to add or update records'
            : 'Failed to save lead',
          'error'
        );
      }
    } catch (err) {
      console.error('Save error:', err);
      showSnackBar('Something went wrong', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [
    formPhone,
    leadFormId,
    dynamicFormData,
    userDetailByNumber,
    leadDetails,
    isBlankCreateMode,
    uploadFileInS3,
    updateStatusInLoop,
    setLeadDetail,
    setFormEnable,
    recordPermissions.canCreate,
    recordPermissions.canEdit,
    showSnackBar,
  ]);

  const editForms = useCallback(() => {
    if (!recordPermissions.canEdit) {
      showSnackBar('You do not have permission to edit records', 'error');
      return;
    }
    setFormEnable(false);
  }, [recordPermissions.canEdit, setFormEnable, showSnackBar]);

  const createLead = useCallback(() => {
    if (!recordPermissions.canCreate) return;
    setIsBlankCreateMode(true);
    setLeadDetail({});
    setFormEnable(false);
    setFormPhone(phone || '');
    setPhoneError('');
    initBlankForm(leadCampaign?.webForm, leadCampaign?.campaign);
  }, [initBlankForm, leadCampaign, phone, recordPermissions.canCreate, setFormEnable, setLeadDetail]);

  const discardFormChanges = useCallback(() => {
    setFieldErrors({});
    setStatusUpdate({});

    if (isBlankCreateMode || !leadDetails?.id) {
      setLeadDetail({});
      setIsBlankCreateMode(false);
      setIsFormDirty(false);
      return;
    }

    processLeadData(leadDetails, leadCampaign?.webForm);
    setFormEnable(true);
  }, [
    isBlankCreateMode,
    leadCampaign?.webForm,
    leadDetails,
    processLeadData,
    setFormEnable,
    setLeadDetail,
  ]);

  const invalidateLoadedContext = useCallback(() => {
    lastLoadedContextRef.current = '';
  }, []);

  const handleLeadSelect = useCallback((leadData) => {
    processLeadData(leadData, leadCampaign?.webForm);
  }, [leadCampaign?.webForm, processLeadData]);

  const updateUserDetail = useCallback(async () => {
    if (!userRawLeadData?.data?.length) return;
    setIsLoading(true);
    try {
      const data = { data: userRawLeadData.data, phone_number: phone };
      const res = userRawLeadData?.isCreateNewUser
        ? await leadFormActions.createUserDetail(data)
        : await leadFormActions.updateUserDetail(data);
      if (res?.status === 200) {
        showSnackBar('User details saved', 'success');
        await loadLeadByPhone();
      }
    } catch (err) {
      showSnackBar(err?.toString() || 'Failed to save user details', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [userRawLeadData, phone, leadFormActions, loadLeadByPhone, showSnackBar]);

  const submitReferences = useCallback(async (fields) => {
    if (!leadDetails?.id) return;
    await leadFormActions.addReferences(fields, leadDetails.id);
    showSnackBar('References saved', 'success');
  }, [leadDetails?.id, leadFormActions, showSnackBar]);

  const submitQcStatus = useCallback(async () => {
    if (!leadDetails?.id) return;
    await leadFormActions.updateQcStatus(leadDetails);
    showSnackBar('QC status updated', 'success');
  }, [leadDetails, leadFormActions, showSnackBar]);

  return {
    leadCampaign,
    campaignId,
    leadFormId,
    leadDetails,
    userDetailByNumber,
    userRawLeadData,
    dynamicFormData,
    formPhone,
    setFormPhone,
    phoneError,
    fieldErrors,
    formEnable,
    isLoading,
    formDataLoading,
    hasCampaignContext,
    applyCampaignFormSelection,
    selectLeadFromList,
    handleFieldChange,
    handlePhoneChange,
    saveForms,
    editForms,
    createLead,
    invalidateLoadedContext,
    handleLeadSelect,
    updateUserDetail,
    submitReferences,
    submitQcStatus,
    processLeadData,
    loadLeadByPhone,
    listRefreshToken,
    canCreateLead: recordPermissions.canCreate,
    canEditLead: recordPermissions.canEdit,
    canAssignLead: recordPermissions.canAssignLead,
    isBlankCreateMode,
    isFormDirty,
    discardFormChanges,
  };
}
